/**
 * Car Lease vs Buy Calculator — educational comparison model.
 * Loan payment: standard amortizing (end-of-period).
 * Lease: depreciation + rent charge with money factor; tax on base payment.
 */

export const DEFAULT_CAR_INPUTS = Object.freeze({
  vehiclePrice: '45000',
  salesTaxRate: '7.5',
  comparisonYears: '8',
  annualDepreciationRate: '15',
  buyDownPayment: '5000',
  loanApr: '6.5',
  loanTermMonths: '60',
  buyDocFee: '450',
  buyRegistrationFee: '650',
  annualMaintenanceBuy: '900',
  leaseDownPayment: '3000',
  leaseMoneyFactor: '0.0025',
  leaseTermMonths: '36',
  leaseResidualRate: '58',
  leaseAcquisitionFee: '695',
  leaseDispositionFee: '395',
  leaseDocFee: '450',
  leaseRegistrationFee: '650',
  annualMaintenanceLease: '300',
});

const MAX_COMPARISON_YEARS = 15;

function parseNumber(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseNonNegative(value) {
  const n = parseNumber(value);
  if (n == null || n < 0) return null;
  return n;
}

function parsePositive(value) {
  const n = parseNonNegative(value);
  if (n == null || n <= 0) return null;
  return n;
}

/** Standard amortizing monthly payment. Handles 0% APR. */
export function monthlyLoanPayment(principal, annualRate, months) {
  if (!(principal >= 0) || !(months > 0) || !Number.isFinite(principal) || !Number.isFinite(annualRate)) {
    return null;
  }
  const monthlyRate = annualRate / 12;
  if (monthlyRate === 0) return principal / months;
  return (principal * monthlyRate) / (1 - (1 + monthlyRate) ** -months);
}

/** Approximate APR equivalent of a money factor (money factor × 2,400). */
export function moneyFactorToAprPercent(moneyFactor) {
  if (!Number.isFinite(moneyFactor) || moneyFactor < 0) return null;
  return moneyFactor * 2400;
}

export function residualValue(vehiclePrice, residualRatePercent) {
  if (!(vehiclePrice >= 0) || !Number.isFinite(residualRatePercent)) return null;
  return vehiclePrice * (residualRatePercent / 100);
}

export function estimatedVehicleValue(vehiclePrice, annualDepreciationRate, year) {
  if (!(vehiclePrice >= 0) || !(annualDepreciationRate >= 0) || !(year >= 0)) return null;
  return vehiclePrice * (1 - annualDepreciationRate) ** year;
}

/**
 * Remaining balance after `paymentsMade` amortizing payments.
 * Never returns a negative balance.
 */
export function remainingLoanBalance(principal, annualRate, months, paymentsMade) {
  if (!(principal >= 0) || !(months > 0) || paymentsMade < 0) return null;
  if (paymentsMade >= months) return 0;

  const payment = monthlyLoanPayment(principal, annualRate, months);
  if (payment == null) return null;

  let balance = principal;
  const monthlyRate = annualRate / 12;
  const count = Math.min(paymentsMade, months);

  for (let i = 0; i < count; i += 1) {
    const interest = balance * monthlyRate;
    const principalPaid = payment - interest;
    balance = Math.max(0, balance - principalPaid);
  }

  return balance;
}

function collectValidationErrors(parsed) {
  const errors = [];
  const {
    vehiclePrice,
    salesTaxRate,
    comparisonYears,
    annualDepreciationRate,
    buyDownPayment,
    loanApr,
    loanTermMonths,
    buyDocFee,
    buyRegistrationFee,
    annualMaintenanceBuy,
    leaseDownPayment,
    leaseMoneyFactor,
    leaseTermMonths,
    leaseResidualRate,
    leaseAcquisitionFee,
    leaseDispositionFee,
    leaseDocFee,
    leaseRegistrationFee,
    annualMaintenanceLease,
  } = parsed;

  const required = {
    vehiclePrice,
    salesTaxRate,
    comparisonYears,
    annualDepreciationRate,
    buyDownPayment,
    loanApr,
    loanTermMonths,
    buyDocFee,
    buyRegistrationFee,
    annualMaintenanceBuy,
    leaseDownPayment,
    leaseMoneyFactor,
    leaseTermMonths,
    leaseResidualRate,
    leaseAcquisitionFee,
    leaseDispositionFee,
    leaseDocFee,
    leaseRegistrationFee,
    annualMaintenanceLease,
  };

  for (const [key, value] of Object.entries(required)) {
    if (value == null) {
      errors.push(`Invalid or empty input: ${key}`);
    }
  }

  if (vehiclePrice != null && vehiclePrice <= 0) {
    errors.push('Vehicle price must be greater than zero');
  }
  if (buyDownPayment != null && vehiclePrice != null && buyDownPayment > vehiclePrice) {
    errors.push('Buy down payment cannot exceed vehicle price');
  }
  if (leaseDownPayment != null && vehiclePrice != null && leaseDownPayment > vehiclePrice) {
    errors.push('Lease down payment cannot exceed vehicle price');
  }
  if (loanTermMonths != null && loanTermMonths <= 0) {
    errors.push('Loan term must be greater than zero');
  }
  if (leaseTermMonths != null && leaseTermMonths <= 0) {
    errors.push('Lease term must be greater than zero');
  }
  if (leaseResidualRate != null && leaseResidualRate > 100) {
    errors.push('Residual percentage cannot exceed 100%');
  }
  if (annualDepreciationRate != null && annualDepreciationRate >= 100) {
    errors.push('Depreciation rate must be less than 100%');
  }

  return errors;
}

function parseInputs(input) {
  const v = { ...DEFAULT_CAR_INPUTS, ...input };
  return {
    vehiclePrice: parsePositive(v.vehiclePrice),
    salesTaxRate: parseNonNegative(v.salesTaxRate),
    comparisonYears: parsePositive(v.comparisonYears),
    annualDepreciationRate: parseNonNegative(v.annualDepreciationRate),
    buyDownPayment: parseNonNegative(v.buyDownPayment),
    loanApr: parseNonNegative(v.loanApr),
    loanTermMonths: parsePositive(v.loanTermMonths),
    buyDocFee: parseNonNegative(v.buyDocFee),
    buyRegistrationFee: parseNonNegative(v.buyRegistrationFee),
    annualMaintenanceBuy: parseNonNegative(v.annualMaintenanceBuy),
    leaseDownPayment: parseNonNegative(v.leaseDownPayment),
    leaseMoneyFactor: parseNonNegative(v.leaseMoneyFactor),
    leaseTermMonths: parsePositive(v.leaseTermMonths),
    leaseResidualRate: parseNonNegative(v.leaseResidualRate),
    leaseAcquisitionFee: parseNonNegative(v.leaseAcquisitionFee),
    leaseDispositionFee: parseNonNegative(v.leaseDispositionFee),
    leaseDocFee: parseNonNegative(v.leaseDocFee),
    leaseRegistrationFee: parseNonNegative(v.leaseRegistrationFee),
    annualMaintenanceLease: parseNonNegative(v.annualMaintenanceLease),
  };
}

/**
 * Build a year-by-year lease vs buy projection.
 * Returns null when assumptions are impossible.
 */
export function buildCarLeaseBuyProjection(input = {}) {
  const parsed = parseInputs(input);
  const errors = collectValidationErrors(parsed);
  if (errors.length > 0) return null;

  const vehiclePrice = parsed.vehiclePrice;
  const taxRate = parsed.salesTaxRate / 100;
  const years = Math.max(1, Math.min(MAX_COMPARISON_YEARS, Math.round(parsed.comparisonYears)));
  const depreciation = parsed.annualDepreciationRate / 100;
  const loanApr = parsed.loanApr / 100;
  const loanMonths = Math.round(parsed.loanTermMonths);
  const buyDown = parsed.buyDownPayment;
  const buyDocFee = parsed.buyDocFee;
  const buyRegistrationFee = parsed.buyRegistrationFee;
  const buyFees = buyDocFee + buyRegistrationFee;
  const leaseMonths = Math.round(parsed.leaseTermMonths);
  const leaseDown = parsed.leaseDownPayment;
  const moneyFactor = parsed.leaseMoneyFactor;
  const residualRate = parsed.leaseResidualRate / 100;
  const leaseAcquisitionFee = parsed.leaseAcquisitionFee;
  const leaseDocFee = parsed.leaseDocFee;
  const leaseRegistrationFee = parsed.leaseRegistrationFee;
  const leaseStartFees = leaseAcquisitionFee + leaseDocFee + leaseRegistrationFee;
  const dispositionFee = parsed.leaseDispositionFee;
  const annualMaintenanceBuy = parsed.annualMaintenanceBuy;
  const annualMaintenanceLease = parsed.annualMaintenanceLease;

  // Purchase
  const buyTax = vehiclePrice * taxRate;
  const buyAmountFinanced = Math.max(0, vehiclePrice + buyTax + buyFees - buyDown);
  const buyMonthly = monthlyLoanPayment(buyAmountFinanced, loanApr, loanMonths);
  if (buyMonthly == null || !Number.isFinite(buyMonthly)) return null;

  const buyTotalLoanPayments = buyMonthly * loanMonths;
  const buyFinanceCharge = Math.max(0, buyTotalLoanPayments - buyAmountFinanced);

  // Lease
  const residual = vehiclePrice * residualRate;
  const leaseAdjustedCapCost = vehiclePrice + leaseStartFees - leaseDown;
  const leaseDepreciationCharge = (leaseAdjustedCapCost - residual) / leaseMonths;
  const leaseRentCharge = (leaseAdjustedCapCost + residual) * moneyFactor;
  const leaseBaseMonthly = leaseDepreciationCharge + leaseRentCharge;
  const leaseMonthlyTax = leaseBaseMonthly * taxRate;
  const leaseMonthly = leaseBaseMonthly * (1 + taxRate);
  const leaseFinanceCharge = leaseRentCharge * leaseMonths;
  const equivalentApr = moneyFactorToAprPercent(moneyFactor);

  const totals = [
    buyMonthly,
    leaseMonthly,
    residual,
    leaseAdjustedCapCost,
    leaseDepreciationCharge,
    leaseRentCharge,
    equivalentApr,
  ];
  if (totals.some((x) => !Number.isFinite(x))) return null;

  let loanBalance = buyAmountFinanced;
  let buyCashPaid = buyDown;
  let leaseCashPaid = 0;
  let currentLeaseMonth = 0;
  const rows = [];
  const totalMonths = years * 12;

  for (let month = 1; month <= totalMonths; month += 1) {
    // Start of each lease cycle: down payment + capitalized/upfront fees
    if ((month - 1) % leaseMonths === 0) {
      leaseCashPaid += leaseDown + leaseStartFees;
      currentLeaseMonth = 0;
    }

    currentLeaseMonth += 1;
    leaseCashPaid += leaseMonthly;

    // End of each lease cycle: disposition fee
    if (currentLeaseMonth === leaseMonths) {
      leaseCashPaid += dispositionFee;
    }

    if (month <= loanMonths) {
      const interest = loanBalance * (loanApr / 12);
      const principalPaid = buyMonthly - interest;
      loanBalance = Math.max(0, loanBalance - principalPaid);
      buyCashPaid += buyMonthly;
    }

    if (month % 12 === 0) {
      const year = month / 12;
      const vehicleValue = estimatedVehicleValue(vehiclePrice, depreciation, year);
      const equity = Math.max(0, vehicleValue - loanBalance);
      // Fees/tax are included via amount financed + down payment (no double-count).
      const buyNetCost = buyCashPaid + annualMaintenanceBuy * year - equity;
      const leaseNetCost = leaseCashPaid + annualMaintenanceLease * year;
      const difference = leaseNetCost - buyNetCost;
      const lowerCostOption = buyNetCost <= leaseNetCost ? 'buy' : 'lease';

      rows.push({
        year,
        buyNetCost,
        leaseNetCost,
        difference,
        lowerCostOption,
        vehicleValue,
        loanBalance,
        equity,
      });
    }
  }

  const breakEvenRow = rows.find((row) => row.buyNetCost <= row.leaseNetCost) || null;
  const final = rows[rows.length - 1];
  const winner = final.buyNetCost <= final.leaseNetCost ? 'buy' : 'lease';

  return {
    years,
    buy: {
      monthly: buyMonthly,
      amountFinanced: buyAmountFinanced,
      tax: buyTax,
      fees: buyFees,
      docFee: buyDocFee,
      registrationFee: buyRegistrationFee,
      totalLoanPayments: buyTotalLoanPayments,
      financeCharge: buyFinanceCharge,
      aprPercent: parsed.loanApr,
      downPayment: buyDown,
    },
    lease: {
      monthly: leaseMonthly,
      baseMonthly: leaseBaseMonthly,
      monthlyTax: leaseMonthlyTax,
      depreciationCharge: leaseDepreciationCharge,
      rentCharge: leaseRentCharge,
      adjustedCapCost: leaseAdjustedCapCost,
      residualValue: residual,
      startFees: leaseStartFees,
      acquisitionFee: leaseAcquisitionFee,
      docFee: leaseDocFee,
      registrationFee: leaseRegistrationFee,
      dispositionFee,
      financeCharge: leaseFinanceCharge,
      moneyFactor,
      equivalentApr,
      downPayment: leaseDown,
      termMonths: leaseMonths,
    },
    rows,
    breakEvenYear: breakEvenRow ? breakEvenRow.year : null,
    final,
    winner,
    savings: Math.abs(final.leaseNetCost - final.buyNetCost),
  };
}
